import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabaseClient';

const { FiBell, FiCalendar, FiTruck, FiCheckCircle, FiAlertTriangle, FiMail, FiSmartphone, FiClock, FiPlus, FiTrash2, FiInfo } = FiIcons;

const Reminders = () => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    company: '',
    email: '',
    phone: ''
  });
  
  const [trucks, setTrucks] = useState([
    { id: Date.now(), make: '', model: '', year: '', nextTest: '' }
  ]);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminEmail, setAdminEmail] = useState('cleanairtruckcheck@gmail.com');

  // Fetch admin email settings on mount
  useEffect(() => {
    const settings = localStorage.getItem('ramirez_settings');
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        if (parsed.email) setAdminEmail(parsed.email);
      } catch (e) {
        console.error("Error loading settings", e);
      }
    }
  }, []);

  const handleContactChange = (e) => {
    setContactInfo({ ...contactInfo, [e.target.name]: e.target.value });
  };

  const handleTruckChange = (id, field, value) => {
    setTrucks(trucks.map(truck => 
      truck.id === id ? { ...truck, [field]: value } : truck
    ));
  };

  const addTruck = () => {
    setTrucks([...trucks, { id: Date.now(), make: '', model: '', year: '', nextTest: '' }]);
  };

  const removeTruck = (id) => {
    if (trucks.length > 1) {
      setTrucks(trucks.filter(truck => truck.id !== id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Calculate earliest inspection for summary
    const dates = trucks.map(t => t.nextTest).filter(d => d).sort();
    const nextInspection = dates.length > 0 ? dates[0] : 'Not Scheduled';

    const newSubscriber = {
      id: Date.now(),
      ...contactInfo,
      trucks: trucks,
      fleetSize: `${trucks.length} Trucks`,
      nextInspection: nextInspection,
      status: 'Active',
      joinedDate: new Date().toLocaleDateString()
    };

    try {
      // 1. Save to Local Storage (Admin Panel Database - Subscribers)
      const existingSubs = JSON.parse(localStorage.getItem('ramirez_subscribers') || '[]');
      const updatedSubs = [newSubscriber, ...existingSubs];
      localStorage.setItem('ramirez_subscribers', JSON.stringify(updatedSubs));

      // 2. Also save to Leads for visibility
      const existingLeads = JSON.parse(localStorage.getItem('ramirez_leads') || '[]');
      const truckDetails = trucks.map(t => `${t.year} ${t.make} ${t.model} (Due: ${t.nextTest})`).join('\n');
      
      const newLead = {
        id: Date.now() + 1,
        name: contactInfo.name,
        company: contactInfo.company,
        email: contactInfo.email,
        phone: contactInfo.phone,
        type: 'Reminder',
        status: 'New',
        date: new Date().toLocaleDateString(),
        message: `Registered ${trucks.length} Trucks:\n${truckDetails}`
      };
      
      localStorage.setItem('ramirez_leads', JSON.stringify([newLead, ...existingLeads]));

      // 3. Save to Supabase (Optional)
      if (supabase) {
        // ... Supabase logic ...
      } else {
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission Error:', error);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEmailApp = () => {
    const truckList = trucks.map(t => `- ${t.year} ${t.make} ${t.model}, Due: ${t.nextTest}`).join('%0D%0A');
    const subject = `CONFIRMATION: New Reminder Subscription - ${contactInfo.company || contactInfo.name}`;
    const body = `Please confirm my subscription to Ramirez Mobile Clean Truck Reminders.%0D%0A%0D%0ADETAILS:%0D%0AName: ${contactInfo.name}%0D%0ACompany: ${contactInfo.company}%0D%0APhone: ${contactInfo.phone}%0D%0AEmail: ${contactInfo.email}%0D%0A%0D%0AFLEET DETAILS (${trucks.length}):%0D%0A${truckList}%0D%0A%0D%0A(Please send this email to complete registration)`;
    
    window.location.href = `mailto:${adminEmail}?subject=${subject}&body=${body}`;
  };

  const benefits = [
    { icon: FiClock, title: "Avoid Downtime", description: "Schedule inspections before they become urgent to keep your trucks on the road." },
    { icon: FiAlertTriangle, title: "Prevent Fines", description: "Missed CARB deadlines can result in significant penalties. We help you stay safe." },
    { icon: FiCalendar, title: "Automated Scheduling", description: "Get notified 30, 15, and 7 days before your compliance certification expires." },
    { icon: FiCheckCircle, title: "Peace of Mind", description: "Focus on your business while we track your fleet's regulatory status." }
  ];

  return (
    <div className="bg-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&w=1920&q=80" alt="Calendar and Truck" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-800/90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block p-4 rounded-full bg-yellow-500/20 mb-6"
          >
            <SafeIcon icon={FiBell} className="text-5xl text-yellow-500" />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Never Miss a CARB Compliance Deadline
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Sign up for our free automated reminder service. We'll track your fleet's Clean Truck Check dates and notify you when it's time for inspection.
          </motion.p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Form Section */}
            <div>
              <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 overflow-hidden">
                <div className="bg-blue-600 p-6 text-white">
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <SafeIcon icon={FiBell} /> Subscribe to Alerts
                  </h3>
                  <p className="text-blue-100 mt-2">Get text & email notifications for your fleet.</p>
                </div>
                
                <div className="p-8">
                  {!isSubmitted ? (
                    <form onSubmit={handleSubmit} className="space-y-8">
                      {/* Contact Information */}
                      <div>
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                          <span className="bg-blue-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                          Contact Details
                        </h4>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-400 text-sm font-semibold mb-1">Full Name</label>
                              <input type="text" name="name" required className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-500" placeholder="John Doe" onChange={handleContactChange} />
                            </div>
                            <div>
                              <label className="block text-gray-400 text-sm font-semibold mb-1">Company Name</label>
                              <input type="text" name="company" className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-500" placeholder="JD Trucking" onChange={handleContactChange} />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-400 text-sm font-semibold mb-1">Email Address</label>
                              <input type="email" name="email" required className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-500" placeholder="john@example.com" onChange={handleContactChange} />
                            </div>
                            <div>
                              <label className="block text-gray-400 text-sm font-semibold mb-1">Phone Number</label>
                              <input type="tel" name="phone" required className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-white placeholder-gray-500" placeholder="(555) 123-4567" onChange={handleContactChange} />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-gray-700 pt-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="bg-blue-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                            Vehicle Information
                          </div>
                          <span className="text-xs text-gray-400 font-normal">Add all trucks in your fleet</span>
                        </h4>
                        
                        <div className="space-y-4">
                          {trucks.map((truck, index) => (
                            <motion.div 
                              key={truck.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-gray-700/50 p-4 rounded-xl border border-gray-600 relative group"
                            >
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-bold text-gray-300">Truck #{index + 1}</span>
                                {trucks.length > 1 && (
                                  <button type="button" onClick={() => removeTruck(truck.id)} className="text-gray-500 hover:text-red-400 transition-colors">
                                    <SafeIcon icon={FiTrash2} />
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Year</label>
                                  <input 
                                    type="number" 
                                    placeholder="2020" 
                                    className="w-full bg-gray-800 border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                                    value={truck.year}
                                    onChange={(e) => handleTruckChange(truck.id, 'year', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Make</label>
                                  <input 
                                    type="text" 
                                    placeholder="Peterbilt" 
                                    className="w-full bg-gray-800 border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                                    value={truck.make}
                                    onChange={(e) => handleTruckChange(truck.id, 'make', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Model</label>
                                  <input 
                                    type="text" 
                                    placeholder="389" 
                                    className="w-full bg-gray-800 border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                                    value={truck.model}
                                    onChange={(e) => handleTruckChange(truck.id, 'model', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Next Test Month</label>
                                  <input 
                                    type="month" 
                                    className="w-full bg-gray-800 border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                                    value={truck.nextTest}
                                    onChange={(e) => handleTruckChange(truck.id, 'nextTest', e.target.value)}
                                    required
                                  />
                                </div>
                              </div>
                            </motion.div>
                          ))}
                          
                          <button 
                            type="button" 
                            onClick={addTruck}
                            className="w-full py-3 border-2 border-dashed border-gray-600 rounded-xl text-gray-400 hover:border-blue-500 hover:text-blue-400 transition-colors flex items-center justify-center gap-2 font-medium"
                          >
                            <SafeIcon icon={FiPlus} /> Add Another Truck
                          </button>
                        </div>
                      </div>

                      <button 
                        type="submit" 
                        disabled={isSubmitting}
                        className={`w-full bg-yellow-500 text-gray-900 font-bold py-4 rounded-lg hover:bg-yellow-400 transition-colors shadow-md text-lg mt-6 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {isSubmitting ? 'Processing...' : 'Activate Reminders'}
                      </button>
                      
                      <p className="text-xs text-center text-gray-400 mt-4">
                        By submitting, you agree to receive automated reminders. You can unsubscribe at any time.
                      </p>
                    </form>
                  ) : (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <SafeIcon icon={FiCheckCircle} className="text-5xl text-green-500" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">Registration Complete!</h3>
                      <p className="text-gray-300 mb-6">
                        We have added {trucks.length} vehicle(s) to our reminder system. To complete the process and verify your email, please click the button below.
                      </p>
                      
                      <button 
                        onClick={handleOpenEmailApp}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-bold flex items-center gap-2 mx-auto mb-6 shadow-lg shadow-blue-900/50"
                      >
                        <SafeIcon icon={FiMail} /> Confirm via Email App
                      </button>
                      
                      <p className="text-sm text-gray-500 mb-4">
                        Sends confirmation to: <span className="text-gray-300">{adminEmail}</span>
                      </p>
                      
                      <button 
                        onClick={() => { setIsSubmitted(false); setTrucks([{ id: Date.now(), make: '', model: '', year: '', nextTest: '' }]); }}
                        className="text-blue-400 font-semibold hover:underline text-sm"
                      >
                        Register another fleet
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="flex flex-col justify-center">
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-white mb-4">Why Sign Up?</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Managing a fleet is hard enough without worrying about expiring certifications. Our reminder system is designed specifically for California trucking companies navigating the complex CARB Clean Truck Check requirements.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:bg-gray-750 transition-colors"
                  >
                    <SafeIcon icon={benefit.icon} className="text-3xl text-blue-500 mb-3" />
                    <h4 className="text-xl font-bold text-white mb-2">{benefit.title}</h4>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 p-6 bg-yellow-900/20 rounded-xl border border-yellow-700/50">
                <div className="flex items-start gap-4">
                  <SafeIcon icon={FiAlertTriangle} className="text-3xl text-yellow-500 mt-1" />
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">Did You Know?</h4>
                    <p className="text-gray-300 text-sm">
                      Effective 2024, the CARB Clean Truck Check program requires more frequent testing for nearly all diesel trucks operating in California. Don't get caught off guard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reminders;